<template>
  <div class="h-full flex pb-6 justify-center">
    <Bar
      v-show="fetchPlantUtilisationMetricsAction.isNot(States.LOADING)"
      :data="chartData"
      :options="chartOptions"
      :chart-id="chartId"
      :dataset-id-key="datasetIdKey"
      :plugins="plugins"
      :css-classes="cssClasses"
      :styles="styles"
      :width="width"
      :height="height"
    />

    <AppSpinner
      v-if="fetchPlantUtilisationMetricsAction.is(States.LOADING)"
      class="my-auto"
    />
  </div>
</template>

<script setup lang="ts">
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { usePlantCategoriesStore } from '@/store/plantCategories';
import { useProjectsStore } from '@/store/projects';
import { BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Title, Tooltip } from 'chart.js';
import { computed, ref, watch } from 'vue';
import { Bar } from 'vue-chartjs';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

const props = defineProps<{
  projectId: number;
  selectedDate: string;
}>();

const projectsStore = useProjectsStore();
const categoriesStore = usePlantCategoriesStore();
const fetchPlantUtilisationMetricsAction = useStoreApiAction(projectsStore.selfPerformDashboard);
// Chart configuration
const chartId = 'plant-utilisation-chart';
const datasetIdKey = 'label';
const plugins = [];
const cssClasses = '';
const styles = {};
const width = undefined;
const height = undefined;
const plantMetrics = ref({});

const chartData = computed(() => {
  const categoryIds = [];
  const usedHours = [];
  const unusedHours = [];

  for(const key in plantMetrics.value) {
    categoryIds.push(parseInt(key));
    usedHours.push((plantMetrics.value[key].used / plantMetrics.value[key].total) * 100);

    unusedHours.push(
      ((plantMetrics.value[key].total - plantMetrics.value[key].used) / plantMetrics.value[key].total) * 100,
    );
  }

  const labels = categoriesStore.models.find(categoryIds).map((category) => category.name);

  return {
    labels,
    datasets: [{
      label: 'Plant Utilisation',
      data: usedHours,
      backgroundColor: '#19428d',
      borderColor: '#19428d',
      borderWidth: 1,
    }, {
      label: 'Plant Not Utilised',
      data: unusedHours,
      backgroundColor: '#ffa010',
      borderColor: '#ffa010',
      borderWidth: 1,
    }],
  };
});

const chartOptions = {
  plugins: {
    title: {
      display: false,
    },

    legend: {
      display: true,
    },
  },

  scales: {
    y: {
      min: 0,
      max: 100,
      stacked: true,
    },

    x: {
      stacked: true,
    },
  },
};

watch([() => props.projectId, () => props.selectedDate], ([projectId, selectedDate]) => {
  if(projectId !== undefined) {
    fetchPlantUtilisationMetricsAction.request(projectId, { date: selectedDate }).then((metrics) => {
      plantMetrics.value = metrics;
    });
  }
}, { immediate: true });
</script>

<style scoped></style>
